<i18n src="@/locales/modules/TitleTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		destroy-on-close
		:before-close="setDialogHide => handleCloseDialog(setDialogHide, isFormDirty)"
		class="custom-dialog"
		:title="$t(dataId ? 'UpdateTitle' : 'AddNewTitle')"
		:visible.sync="isPopupActiveLocal"
	>
		<PerfectScrollbar ref="formScrollBar" class="scroll-area--data-list" :settings="settings">
			<div class="p-6">
				<!-- NAME -->
				<label for class="vs-input--label">{{ $t("TitleName") }} <span class="text-danger">*</span>:</label>
				<vs-input :placeholder="$t('Placeholder.InputName')" v-model="dataName" class="w-full" name="name" :data-vv-as="$t('TitleName')" v-validate="'required'" />
				<span class="text-danger text-sm" v-show="errors.has('name')">{{ errors.first("name") }}</span>

				<!-- DEPARTMENT -->
				<div class="my-5">
					<label for class="vs-input--label">{{ $t("Department") }} <span class="text-danger">*</span>:</label>
					<v-select
						class="select-tree"
						:placeholder="`${$t('Actions.Choose')} ${$t('Placeholder.InputParent').toLowerCase()}`"
						v-validate="'required'"
						:data-vv-as="$t('Department')"
						v-model="parentDepartmentId"
						:reduce="department => department.value"
						label="text"
						name="department"
						:options="departmentChoices"
					>
						<template v-slot:option="option">
							<div :class="`flex items-center pl-${option.depth > 1 ? option.depth * 2 : option.depth}`">
								<i class="mr-2 nucleo nucleo-shape-oval-3"></i>
								{{ option.text }}
							</div>
						</template>
						<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
					</v-select>
					<span class="text-danger text-sm" v-show="errors.has('department')">{{ errors.first("department") }}</span>
				</div>

				<!-- ROLE -->
				<div class="my-5">
					<label for class="vs-input--label">{{ $t("Role") }} <span class="text-danger">*</span>:</label>
					<v-select
						class="select-tree"
						:placeholder="`${$t('Actions.Choose')} ${$t('Role').toLowerCase()}`"
						v-model="roleId"
						:reduce="role => role.value"
						label="text"
						name="role"
						v-validate="'required'"
						:data-vv-as="$t('Role')"
						:options="roleChoices"
					>
						<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
					</v-select>
					<span class="text-danger text-sm" v-show="errors.has('role')">{{ errors.first("role") }}</span>
				</div>

				<!-- Description -->
				<div class="my-5">
					<label for class="vs-input--label">{{ $t("Description") }}:</label>
					<vs-textarea class="mb-0" :placeholder="$t('Placeholder.InputDescription')" v-model="description" />
				</div>

				<!-- Is Leader -->
				<div class="mt-5">
					<vs-checkbox class="inline-flex ml-0" v-model="isLead">{{ $t("IsLeader") }}</vs-checkbox>
				</div>
			</div>
		</PerfectScrollbar>

		<div class="flex justify-center" slot="footer">
			<vs-button :type="data.id ? 'filled' : 'border'" class="w-full sm:w-1/3 mb-3 sm:mb-0 sm:mr-6 font-bold" @click.stop="submitData(!!data.id)">
				{{ data.id ? $t("Actions.Save") : $t("Actions.SaveAndRecreate") }}
			</vs-button>
			<vs-button v-show="!data.id" class="w-full sm:w-1/3 font-bold" @click="submitData">{{ $t("Actions.Submit") }}</vs-button>
		</div>
	</el-dialog>
</template>

<script>
import vSelect from "vue-select";
import { mapGetters } from "vuex";
export default {
	props: {
		isPopupActive: {
			type: Boolean,
			required: true
		},
		data: {
			type: Object,
			default: () => {}
		}
	},
	components: {
		vSelect
	},
	data() {
		return {
			dataId: null,
			dataName: "",
			roleId: null,
			parentDepartmentId: null,
			description: "",
			isLead: false,

			initFormData: {},
			isFormDirty: false,

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			}
		};
	},
	watch: {
		isPopupActive(val) {
			if (!val) return;

			const { id, name, department, description, role, is_lead } = this.data;
			this.roleId = role ? role.id : null;
			this.parentDepartmentId = department ? department.id : null;
			if (!this.data.id) {
				this.initValues();
			} else {
				this.dataId = id;
				this.dataName = name;
				this.isLead = !!is_lead;
				this.description = description;
			}

			this.isFormDirty = false;
			this.initFormData = {
				dataId: this.dataId,
				roleId: this.roleId,
				isLead: this.isLead,
				dataName: this.dataName,
				parentDepartmentId: this.parentDepartmentId,
				description: this.description
			};
		},
		computedFormData: {
			handler: function(newVal) {
				this.isFormDirty = JSON.stringify(newVal) !== JSON.stringify(this.initFormData) && newVal.dataId === this.initFormData.dataId;
			},
			deep: true
		}
	},
	methods: {
		initValues() {
			if (this.data.id) return;
			this.dataId = null;
			this.dataName = "";
			this.roleId = null;
			this.isLead = false;
			this.parentDepartmentId = null;
			this.description = "";
			this.$validator.reset();
		},
		handleCreateOrUpdateSuccess() {
			this.initValues();
			if (this.dataId) {
				this.$emit("closePopup");
			}
			this.isFormDirty = false;
			this.$helper.notifySuccess({
				context: this,
				options: {
					text: this.$t(`${this.dataId ? "UpdateTitleSuccessful" : "CreateTitleSuccessful"}`)
				}
			});
		},
		async handleCreateOrUpdateTitle(dataObj) {
			try {
				this.$vs.loading();
				if (this.dataId) {
					await this.$store.dispatch("titleManagement/updateTitle", {
						...dataObj,
						id: this.dataId
					});
				} else {
					await this.$store.dispatch("titleManagement/addTitle", dataObj);
				}

				this.handleCreateOrUpdateSuccess();
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$vs.loading.close();
			}
		},
		submitData() {
			this.$validator.validateAll().then(result => {
				if (result) {
					const dataObj = {
						name: this.dataName,
						is_lead: this.isLead,
						role_id: this.roleId ? this.roleId : 0,
						department_id: this.parentDepartmentId,
						description: this.description
					};
					this.handleCreateOrUpdateTitle(dataObj);
				}
			});
		}
	},
	computed: {
		isPopupActiveLocal: {
			get() {
				return this.isPopupActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closePopup");
				}
			}
		},
		roleChoices() {
			return (this.$store.state.titleManagement.roles || []).map(role => ({
				value: role.id,
				text: role.name
			}));
		},
		departmentChoices() {
			return (this.getFormattedFlattenTreeDepartments || []).map(department => ({
				value: department.id,
				text: department.name,
				depth: department.depth
			}));
		},
		...mapGetters({
			getFormattedFlattenTreeDepartments: "departmentManagement/getFormattedFlattenTreeDepartments"
		}),
		computedFormData() {
			return {
				dataId: this.dataId,
				roleId: this.roleId,
				isLead: this.isLead,
				dataName: this.dataName,
				parentDepartmentId: this.parentDepartmentId,
				description: this.description
			};
		}
	},
	created() {
		this.$store.dispatch("departmentManagement/fetchDepartments", {});
	}
};
</script>

<style lang="scss" scoped>
::v-deep header {
	&.vs-popup--header {
		h3 {
			text-transform: uppercase;
		}
	}
}
</style>
